import React from "react";
import { Link } from "react-router-dom";
import {
  Gauge, Search, ShieldCheck, Smartphone, Globe, ArrowRight,
  CheckCircle2, Zap, Clock, FileSearch, MapPin, LineChart,
} from "lucide-react";
import { Eyebrow, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { WebsiteCheckForm } from "@/components/WebsiteCheckForm";
import { useI18n } from "@/context/I18nContext";

const SITE_URL = "https://it-servicepoint.eu";

const FACTORS = [
  { icon: Gauge, title: "Ladezeit & Performance", desc: "Wir messen die echte Server-Antwortzeit. Jede Sekunde länger kostet bis zu 20 % Conversion." },
  { icon: ShieldCheck, title: "HTTPS / SSL-Verschlüsselung", desc: "Pflicht für DSGVO, Vertrauen und Google-Ranking. Ohne SSL: kein Top-10." },
  { icon: Smartphone, title: "Mobile-Optimierung", desc: "Über 70 % Ihrer Besucher kommen vom Smartphone. Wir prüfen Viewport und Responsive-Setup." },
  { icon: FileSearch, title: "Meta-Title & Description", desc: "Ihre Visitenkarte in Google. Wir bewerten Länge, Relevanz und Keyword-Fokus." },
  { icon: Search, title: "Überschriften-Struktur (H1)", desc: "Eine eindeutige H1 pro Seite ist SEO-Pflicht. Wir prüfen, ob das passt." },
  { icon: Globe, title: "Social Sharing & Open Graph", desc: "Wie sehen Ihre Links auf LinkedIn, Facebook und WhatsApp aus? Mit oder ohne Vorschau-Karte?" },
  { icon: LineChart, title: "Alt-Texte für Bilder", desc: "Wichtig für Barrierefreiheit, Bildersuche und SEO. Wir prüfen den Anteil." },
  { icon: Zap, title: "robots.txt & sitemap.xml", desc: "Die Steuerung für Google. Fehlen sie, verschenken Sie wertvolles Crawl-Budget." },
];

const STEPS = [
  { step: "01", title: "URL eingeben", body: "Tippen Sie Ihre Domain ein. Keine Anmeldung, kein Login, keine versteckten Kosten." },
  { step: "02", title: "Sofort-Ergebnis", body: "In rund 60 Sekunden bekommen Sie Ihren Score von 0–100 und alle Detail-Checks auf den Bildschirm." },
  { step: "03", title: "E-Mail-Report", body: "Den vollständigen Report mit Empfehlungen senden wir Ihnen direkt per E-Mail – zum Speichern und Teilen." },
  { step: "04", title: "Kostenloses Erstgespräch", body: "Auf Wunsch besprechen wir die Ergebnisse in einem 30-minütigen Termin und liefern einen Maßnahmenplan." },
];

const FAQ = [
  {
    q: "Ist der Webseiten-Check wirklich kostenlos?",
    a: "Ja, vollständig kostenlos und unverbindlich. Wir berechnen nur, wenn Sie sich später bewusst für eine Beauftragung entscheiden.",
  },
  {
    q: "Welche Daten werden gespeichert?",
    a: "Ihre eingegebene URL, E-Mail-Adresse und der Report werden in unserer Datenbank in Deutschland gespeichert, damit wir Ihnen das Ergebnis zusenden und auf Wunsch nachfragen können. Details in der Datenschutzerklärung.",
  },
  {
    q: "Bekomme ich Werbung nach dem Check?",
    a: "Nein. Wir melden uns einmal mit dem Ergebnis und dem Angebot eines kostenfreien Erstgesprächs. Kein Newsletter, kein Spam – Versprochen.",
  },
  {
    q: "Was, wenn meine Webseite schlecht abschneidet?",
    a: "Genau dafür ist der Check da. Wir zeigen Ihnen, was im Argen liegt und liefern einen konkreten Plan, wie es besser wird – mit Festpreis-Angebot.",
  },
  {
    q: "Wie genau sind die Ergebnisse?",
    a: "Unser Basis-Check deckt die wichtigsten 12 SEO-, Performance- und Sicherheits-Faktoren ab. Für eine vollständige Tiefen-Analyse mit Google Core Web Vitals führen wir auf Wunsch ein Profi-Audit durch.",
  },
];

export const WebsiteCheck = () => {
  const { lang } = useI18n();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Kostenloser Webseiten-Check",
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: "DE",
      serviceType: "Website analysis",
      description: "Kostenfreier 60-Sekunden-Check Ihrer Webseite – Performance, SEO, Mobile, HTTPS und Sicherheit. Mit Detail-Report per E-Mail.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Webseiten-Check", item: `${SITE_URL}/webseiten-check` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="website-check-page" className="relative">
      <SEO
        title={lang === "de"
          ? "Kostenloser Webseiten-Check · SEO, Performance & Mobile in 60 Sekunden"
          : "Free Website Check · SEO, Performance & Mobile in 60 Seconds"}
        description={lang === "de"
          ? "Wie gut ist Ihre Webseite wirklich? Kostenfreier Check in 60 Sekunden – Performance, SEO, HTTPS, Mobile, Meta-Tags und mehr. Mit persönlichem Report per E-Mail."
          : "How good is your website really? Free 60-second check – performance, SEO, HTTPS, mobile, meta tags and more. With personal email report."}
        keywords="Webseiten Check, Website Analyse, SEO Check, Performance Check, Webseite testen, kostenloser Webseiten Test, Webseiten Audit Fulda, SEO Analyse, Mobile Check, HTTPS Check"
        jsonLd={jsonLd}
        type="service"
      />

      {/* HERO + FORM */}
      <section className="relative overflow-hidden bg-soft border-b border-line">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <Eyebrow>Kostenloser Webseiten-Check</Eyebrow>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-ink">
              Wie gut ist Ihre Webseite <span className="text-primary">wirklich?</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-muted leading-relaxed">
              In <strong className="text-ink">60 Sekunden</strong> erfahren Sie, wo Ihre Webseite steht: Performance, SEO, Mobile, Sicherheit und mehr. Mit konkreten Empfehlungen, was zu tun ist – ohne Verkaufs-Folien.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "12 Faktoren, geprüft in Echtzeit",
                "Score von 0 bis 100 + Note A–F",
                "Detail-Report per E-Mail",
                "Keine Anmeldung, kein Newsletter",
              ].map((it) => (
                <li key={it} className="flex items-center gap-3 text-ink">
                  <CheckCircle2 size={18} className="text-primary shrink-0" /> {it}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex items-center gap-6 text-sm text-muted">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-primary" />
                <span>~60 s Dauer</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-primary" />
                <span>Server in Deutschland</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <WebsiteCheckForm />
          </div>
        </div>
      </section>

      {/* FACTORS */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Was wir prüfen"
            title="12 Faktoren, die wirklich zählen."
            sub="Wir konzentrieren uns auf die Themen, die direkt Ihren Umsatz beeinflussen: Geschwindigkeit, Auffindbarkeit, mobile Nutzbarkeit und Vertrauen."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FACTORS.map((f, i) => (
              <div key={f.title} className="border border-line bg-white p-7 lift" data-testid={`check-factor-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <f.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink">{f.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="So läuft es ab"
            title="In 4 Schritten zum Report."
          />
          <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <li key={s.step} className="border border-line bg-white p-7" data-testid={`check-step-${i}`}>
                <span className="font-display font-black text-4xl text-accent">{s.step}</span>
                <h4 className="mt-4 font-display font-bold text-lg text-ink">{s.title}</h4>
                <p className="mt-3 text-muted text-sm leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink">FAQ.</h2>
          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="border border-line bg-white p-6 group" data-testid={`check-faq-${i}`}>
                <summary className="font-display font-bold text-lg text-ink cursor-pointer flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-4 text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tightest leading-[1.05] max-w-3xl mx-auto">
            Bereit für eine Webseite, die liefert?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">
            Wenn der Check Schwächen offenbart hat: Wir bauen Ihnen eine neue Webseite – professionell, schnell, mit lokalem SEO. Festpreis-Angebot in 3 Werktagen.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/webdesign"
              data-testid="check-cta-webdesign"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              Webdesign-Leistungen ansehen <ArrowRight size={18} />
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              Kostenloses Erstgespräch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
